import { registerDestructor } from '@ember/destroyable';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { scheduleOnce } from '@ember/runloop';
import type { ArgsFor } from 'ember-modifier';
import Modifier from 'ember-modifier';

function cleanup(instance: AutoresizeModifier) {
  const { element } = instance;
  /* eslint-disable @typescript-eslint/unbound-method */
  element!.removeEventListener('input', instance.scheduleResize);
  element!.removeEventListener('change', instance.scheduleResize);
  element!.removeEventListener('cut', instance.scheduleResize);
  element!.removeEventListener('paste', instance.scheduleResize);
  element!.removeEventListener('drop', instance.scheduleResize);
  element!.removeEventListener('keydown', instance.scheduleResize);
  /* eslint-enable @typescript-eslint/unbound-method */
}

function resize(element: HTMLElement): void {
  element.style.height = 'auto';
  element.style.height = element.scrollHeight + 2 + 'px';
}

interface AutoresizeModifierSignature {
  Args: {
    Named: {
      maxHeight: string;
    };
    Positional: unknown[];
  };
  Element: HTMLElement;
}

export default class AutoresizeModifier extends Modifier<AutoresizeModifierSignature> {
  element: HTMLElement | null = null;

  constructor(owner: Owner, args: ArgsFor<AutoresizeModifierSignature>) {
    super(owner, args);
    registerDestructor(this, cleanup);
  }

  @action
  scheduleResize(): void {
    scheduleOnce('afterRender', this, resize, this.element!);
  }

  modify(element: HTMLElement, _: unknown[], { maxHeight }: AutoresizeModifierSignature['Args']['Named']): void {
    if (!this.element) {
      // element was just inserted
      this.element = element;

      element.style.maxHeight = maxHeight;
      element.style.overflowY = 'auto';
      element.style.minHeight = 'unset';
      element.style.resize = 'none';

      /* eslint-disable @typescript-eslint/unbound-method */
      element.addEventListener('input', this.scheduleResize);
      element.addEventListener('change', this.scheduleResize);
      element.addEventListener('cut', this.scheduleResize);
      element.addEventListener('paste', this.scheduleResize);
      element.addEventListener('drop', this.scheduleResize);
      element.addEventListener('keydown', this.scheduleResize);
      /* eslint-enable @typescript-eslint/unbound-method */
    }
    this.scheduleResize();
  }
}
